import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useParams, Navigate, useSearchParams } from 'react-router-dom';
import { useDraggable } from '@dnd-kit/core';
// FASE-C (design system): emoji-ícone banido — Lucide only.
import { CalendarDays, Check, HardHat, Settings2, UserRound } from 'lucide-react';

import {
  Badge,
  Button,
  Alert,
  Calendario,
  Input,
  Label,
  PageHeader,
  Select,
  Switch,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogFooter,
} from '@/components/ui';
import { KanbanBoard, type KanbanCardProps } from '@/components/kanban/KanbanBoard';
import {
  KanbanCalendarView,
  type KanbanCalendarItemProps,
} from '@/components/kanban/KanbanCalendarView';
import { KanbanListView, type KanbanListRowProps } from '@/components/kanban/KanbanListView';
import { KanbanViewToggle, type KanbanViewMode } from '@/components/kanban/KanbanViewToggle';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { useKanbanColumns } from '@/lib/use-kanban-columns';
import { useAutomations } from '@/lib/automations-store';
// FASE 3 / M5 (auditoria 2026-05-21): import via barrel compartilhado
// pra evitar acoplamento direto manutencao→comercial.
import { AutomationsPanel } from '@/components/automations';
// BUG-V2-058: TagBadge no header do OsCard (regra 13 padrão Kanban).
import { TagBadge } from '@/components/tags';
import {
  useServiceOrders,
  useServiceOrder,
  useServiceOrderActions,
  type ServiceOrder,
  type OsPrioridade,
  type OsStatus,
} from '@/lib/service-orders-store';
import { useToast } from '@/components/ui';
import {
  useWorkflowByDeptAndSlug,
  useDefaultWorkflowOfDept,
  useWorkflows,
} from '@/lib/workflows-store';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { useUsers } from '@/lib/admin-store';
import { useClientsSearch, type Client } from '@/lib/clients-store';
import { formatRelativeTime } from '@/lib/format-activity';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/cn';
import type { DefaultStage } from '@/lib/use-kanban-columns';
// UX-1 / FEAT-V2-007: modal completo (substitui stub interno).
import { OsDetailDialog } from './OsDetailDialog';
// BUG-B1.6.2: Corretiva agora é deal-based (não service_orders). Branch
// abaixo decide qual Kanban renderizar baseado no slug do sub-workflow.
import { CorretivaPage } from './CorretivaPage';

import { formatDate, formatDataHoraSegundos } from '@/lib/format-date';
/**
 * Modo Gestor de OS — `/menu/manutencao/:workflow?` (R5 + R5.1).
 *
 * R5.1 (Bloco A): refatorado pra usar `<KanbanBoard>` universal.
 * Resolve dívida arquitetural da R5:
 *   - BUG-V2-008: OS cards agora movem via DnD (PointerSensor distance:5)
 *   - BUG-V2-009: admin renomeia colunas via EditableColumnHeader
 *   - BUG-V2-010: admin pode criar colunas custom além das padrão
 *   - (Automações multi-workflow chega no Bloco E — botão fica oculto até lá)
 *
 * Click numa OS abre detalhes inline (TODO próxima rodada: modal completo
 * com abas Atividade/Comentários reusando FEAT-V2-002).
 */
export function ManutencaoPage() {
  const params = useParams();
  const workflowSlug = params.workflow ?? null;

  // Se não tem workflow na URL, redirect pro default da Manutenção
  const defaultWf = useDefaultWorkflowOfDept('manutencao');
  const wf = useWorkflowByDeptAndSlug('manutencao', workflowSlug);
  // FASE-C: um slug que não existe na Manutenção (ex.: /menu/manutencao/quadro,
  // que é sub-rota de COMPRAS) ficava PRA SEMPRE em "Carregando workflow…" —
  // um carregando que mente, sem h1 e sem saída. Agora só é "carregando"
  // enquanto a lista está mesmo em voo; depois disso é o 404 canônico do app.
  const { isLoading: carregandoWorkflows } = useWorkflows({ dept: 'manutencao', ativo: true });

  if (!workflowSlug && defaultWf) {
    return <Navigate to={`/menu/manutencao/${defaultWf.slug}`} replace />;
  }
  if (!wf) {
    if (carregandoWorkflows) {
      return (
        <div className="py-8 text-center">
          <div className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</div>
        </div>
      );
    }
    return <NotFoundPage />;
  }

  // BUG-B1.6.2: Corretiva passa a ler de `deals` (chamados Portal +
  // deals manuais de colaboradores IGM). Preventiva/Vistoria continuam
  // usando service_orders via ManutencaoKanban — branch isolado garante
  // zero regressão nos outros workflows.
  if (wf.slug === 'corretiva') {
    return <CorretivaPage workflow={wf} />;
  }

  return <ManutencaoKanban workflow={wf} />;
}

interface ManutencaoKanbanProps {
  workflow: {
    id: string;
    slug: string;
    nome: string;
    cor: string | null;
    icone: string | null;
    /** Bloco E: responsável do workflow (LEFT JOIN no backend, null se não setado). */
    responsavel?: { id: string; nome: string; fotoUrl: string | null } | null;
  };
}

// Status padrão da OS — defaults pareados pra entrar no KanbanBoard.
// Mesmas 5 colunas do shell anterior, agora editáveis pelo admin (nome) +
// permitindo colunas custom.
const OS_STATUS_DEFAULTS: DefaultStage[] = [
  { slug: 'aberta', label: 'Aberta', ordem: 1, variant: 'neutral' },
  { slug: 'atribuida', label: 'Atribuída', ordem: 2, variant: 'info' },
  { slug: 'em_campo', label: 'Em Campo', ordem: 3, variant: 'warning' },
  { slug: 'concluida', label: 'Concluída', ordem: 4, variant: 'success' },
  { slug: 'cancelada', label: 'Cancelada', ordem: 5, variant: 'danger' },
];

const PRIORIDADE_LABELS: Record<OsPrioridade, string> = {
  baixa: 'Baixa',
  normal: 'Normal',
  alta: 'Alta',
  urgente: 'Urgente',
};

// ─── Context pra OsCard receber callbacks sem prop drilling no KanbanBoard ───

interface OsCardCtxValue {
  onOpen?: (os: ServiceOrder) => void;
}
const OsCardContext = createContext<OsCardCtxValue>({});

function useOsCardContext() {
  return useContext(OsCardContext);
}

function ManutencaoKanban({ workflow }: ManutencaoKanbanProps) {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;
  const { data: ossRaw = [], isLoading, isError } = useServiceOrders({ workflowId: workflow.id });
  const { changeStatus } = useServiceOrderActions();
  const [creating, setCreating] = useState(false);
  const [openOs, setOpenOs] = useState<ServiceOrder | null>(null);
  const [viewMode, setViewMode] = useState<KanbanViewMode>('kanban');
  const [automationsOpen, setAutomationsOpen] = useState(false);
  // BUG-V2-047: toggle "Mostrar inativos" — state local (decisão arquitetural).
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const oss = useMemo(
    () => (mostrarInativos ? ossRaw : ossRaw.filter((o) => o.ativo !== false)),
    [ossRaw, mostrarInativos]
  );

  // ─── AUDITORIA-V2-001 (BUG-V2-050 / C2) — deeplink ?openCard= ────────
  // Pattern portado do ComercialPage. Aceita `?openCard=` (canônico) e
  // `?openDeal=` (compat retroativa pra payloads antigos no banco).
  //
  // M1 cards arquivados: fetch single via useServiceOrder (não filtra ativo)
  // permite abrir card arquivado direto da notif. Modal mostra badge cinza
  // "Inativo" (BUG-V2-047).
  //
  // Sub-workflow handling: se OS pertence a outro subworkflow (`corretiva`
  // mas usuário está na URL de `preventiva`), o lookup single OS funciona
  // independente do filtro de workflow da lista — modal abre normalmente.
  const [searchParams, setSearchParams] = useSearchParams();
  const openCardId =
    searchParams.get('openCard') ?? searchParams.get('openDeal') ?? null;
  const { data: deeplinkOs, isError: deeplinkErr } = useServiceOrder(openCardId);
  const { show: showToast } = useToast();

  useEffect(() => {
    if (!openCardId) return;
    if (deeplinkOs) {
      setOpenOs(deeplinkOs);
      // Limpa o param pra evitar re-trigger no próximo render.
      setSearchParams({}, { replace: true });
    } else if (deeplinkErr) {
      // 404 ou erro real — mensagem amigável.
      showToast('OS não encontrada (pode ter sido deletada ou você perdeu acesso).', {
        variant: 'error',
      });
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCardId, deeplinkOs, deeplinkErr]);

  // workflowSlug pro KanbanBoard precisa ser único por workflow concreto.
  // Composição "{dept}-{workflowSlug}" evita colisão entre Manutenção/Preventiva
  // e Manutenção/Corretiva (que rodam o mesmo componente).
  const kanbanWorkflowKey = useMemo(
    () => `manutencao-${workflow.slug}`,
    [workflow.slug]
  );

  // Automations engine usa MESMA chave do kanban — automations são por
  // sub-workflow concreto, não por departamento inteiro.
  const automationsSlug = kanbanWorkflowKey;
  const automations = useAutomations(automationsSlug);
  const automationsAtivasCount = automations.filter((a) => a.enabled).length;

  // Colunas resolvidas pra alimentar AutomationsPanel (mesma queryKey do
  // KanbanBoard, React Query dedupe).
  const { columns, getLabel: getColumnLabel } = useKanbanColumns(
    kanbanWorkflowKey,
    OS_STATUS_DEFAULTS
  );

  if (isLoading) {
    return (
      <PageShell workflow={workflow} onCreateClick={() => setCreating(true)}>
        <div className="py-8 text-center text-sm text-ink-3 italic">Carregando OSs…</div>
      </PageShell>
    );
  }
  if (isError) {
    return (
      <PageShell workflow={workflow} onCreateClick={() => setCreating(true)}>
        <Alert variant="danger" rail>
          Erro ao carregar OSs. Tente novamente.
        </Alert>
      </PageShell>
    );
  }

  return (
    <OsCardContext.Provider value={{ onOpen: setOpenOs }}>
      <PageShell
        workflow={workflow}
        onCreateClick={() => setCreating(true)}
        viewToggle={
          <KanbanViewToggle value={viewMode} onChange={setViewMode} />
        }
        inativosToggle={
          /* BUG-V2-052: padronizado com Switch do design system —
             mesmo componente usado na ComercialPage. */
          <Switch
            id="manutencao-mostrar-inativos"
            label="Mostrar inativos"
            checked={mostrarInativos}
            onChange={(e) => setMostrarInativos(e.target.checked)}
            data-testid="toggle-mostrar-inativos"
          />
        }
        automationsButton={
          isAdmin ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="manutencao-automations-button"
              onClick={() => setAutomationsOpen(true)}
            >
              <Settings2 className="w-4 h-4 mr-1.5" aria-hidden="true" />
              Automações ({automationsAtivasCount} ativa
              {automationsAtivasCount === 1 ? '' : 's'})
            </Button>
          ) : null
        }
      >
        {viewMode === 'kanban' && (
          <KanbanBoard<ServiceOrder>
            workflowSlug={kanbanWorkflowKey}
            defaultStages={OS_STATUS_DEFAULTS}
            items={oss}
            getItemStage={(os) => os.status}
            cardComponent={OsCard}
            onStageChange={async ({ item, toSlug, fromLabel, toLabel }) => {
              void fromLabel;
              void toLabel;
              try {
                await changeStatus(item.id, toSlug as OsStatus);
              } catch (e) {
                console.warn('[manutencao-kanban] changeStatus falhou:', e);
              }
            }}
            workflowOwner={
              workflow.responsavel
                ? { nome: workflow.responsavel.nome, fotoUrl: workflow.responsavel.fotoUrl }
                : null
            }
            isAdmin={isAdmin}
            emptyState={{
              title: `Nenhuma OS em ${workflow.nome}`,
              description: 'Crie a primeira OS pra começar.',
              action: (
                // MOBILE-COMPLETO (M5): no celular a tela vazia mostrava "+ Nova OS"
                // DUAS vezes (aqui e no cabeçalho, uma embaixo da outra). Lá fica
                // só a do cabeçalho; no computador nada muda.
                <Button type="button" onClick={() => setCreating(true)} className="max-md:hidden">
                  + Nova OS
                </Button>
              ),
            }}
            emptyColumnText="Sem OSs nesta coluna"
          />
        )}

        {viewMode === 'calendar' && (
          <KanbanCalendarView<ServiceOrder>
            items={oss}
            getItemDate={(os) => os.agendadoParaIso}
            itemComponent={OsCalendarItem}
            onItemClick={(os) => setOpenOs(os)}
          />
        )}

        {viewMode === 'list' && (
          <KanbanListView<ServiceOrder>
            items={oss}
            rowComponent={OsListRow}
            onItemClick={(os) => setOpenOs(os)}
            emptyTitle={`Nenhuma OS em ${workflow.nome}`}
            emptyDescription="Crie a primeira OS pra começar."
          />
        )}

        {creating && (
          <NovaOsDialog workflowId={workflow.id} onClose={() => setCreating(false)} />
        )}

        {openOs && <OsDetailDialog os={openOs} onClose={() => setOpenOs(null)} />}

        {isAdmin && (
          <AutomationsPanel
            open={automationsOpen}
            onClose={() => setAutomationsOpen(false)}
            workflowSlug={automationsSlug}
            columns={columns}
            getColumnLabel={getColumnLabel}
            title={`Automações — ${workflow.nome}`}
          />
        )}
      </PageShell>
    </OsCardContext.Provider>
  );
}

// ─── Mini-card de OS no calendário ─────────────────────────────────────

function OsCalendarItem({ item, onClick }: KanbanCalendarItemProps<ServiceOrder>) {
  return (
    <button
      type="button"
      data-testid={`calendar-os-${item.id}`}
      onClick={onClick}
      className={cn(
        'block w-full text-left px-1.5 py-0.5 rounded text-xs truncate transition-colors',
        'bg-paper-warm dark:bg-paper-dark-warm hover:bg-red-soft dark:hover:bg-red-soft-dark',
        'text-ink-1 dark:text-ink-dark-1'
      )}
      title={item.titulo}
    >
      <span className="font-medium">{item.titulo}</span>
    </button>
  );
}

// ─── Linha de OS na lista ──────────────────────────────────────────────

function OsListRow({ item, onClick }: KanbanListRowProps<ServiceOrder>) {
  return (
    <li
      data-testid={`list-os-${item.id}`}
      className="bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark rounded-lg p-3 hover:shadow-sm transition-shadow"
    >
      <button
        type="button"
        onClick={onClick}
        className="w-full text-left flex items-center justify-between gap-3"
        aria-label={`Abrir OS ${item.titulo}`}
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 truncate">
            {item.titulo}
          </p>
          <p className="text-xs text-ink-3 dark:text-ink-dark-3 truncate">
            {item.endereco}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-ink-2 dark:text-ink-dark-2 flex-shrink-0">
          {item.agendadoParaIso && (
            <span className="hidden sm:inline-flex items-center gap-1">
              <CalendarDays className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              {formatDate(item.agendadoParaIso)}
            </span>
          )}
          <Badge variant={statusBadgeVariant(item.status)} size="sm">
            {item.status}
          </Badge>
        </div>
      </button>
    </li>
  );
}

function statusBadgeVariant(
  status: string
): 'neutral' | 'info' | 'warning' | 'success' | 'danger' {
  switch (status) {
    case 'atribuida':
      return 'info';
    case 'em_campo':
      return 'warning';
    case 'concluida':
      return 'success';
    case 'cancelada':
      return 'danger';
    default:
      return 'neutral';
  }
}

// ─── Page shell ────────────────────────────────────────────────────────

function PageShell({
  workflow,
  onCreateClick,
  viewToggle,
  automationsButton,
  inativosToggle,
  children,
}: {
  workflow: ManutencaoKanbanProps['workflow'];
  onCreateClick: () => void;
  viewToggle?: React.ReactNode;
  automationsButton?: React.ReactNode;
  // BUG-V2-047: slot pro toggle "Mostrar inativos".
  inativosToggle?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Manutenção', to: '/menu/manutencao' },
          { label: workflow.nome },
        ]}
        className="mb-2"
      />
      {/* FASE-C (design system): PageHeader canônico — a tela não tinha <h1>
          (dor 9 do diagnóstico) e a fileira de controles estourava +16px no
          375px (dor 7): agora as ações quebram linha dentro do slot `actions`. */}
      <PageHeader
        title={`Manutenção — ${workflow.nome}`}
        subtitle="Ordens de serviço ativas neste fluxo"
        className="mb-4"
        actions={
          <>
            {inativosToggle}
            {automationsButton}
            {viewToggle}
            <Button type="button" onClick={onCreateClick} data-testid="os-new-btn">
              + Nova OS
            </Button>
          </>
        }
      />
      {children}
    </div>
  );
}

// ─── OsCard ────────────────────────────────────────────────────────────

function OsCard({ item: os, column }: KanbanCardProps<ServiceOrder>) {
  const users = useUsers();
  const { onOpen } = useOsCardContext();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: os.id,
  });

  const tecnicoNome = useMemo(
    () => (os.tecnicoId ? users.find((u) => u.id === os.tecnicoId)?.nome ?? os.tecnicoId : null),
    [users, os.tecnicoId]
  );

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 10 }
    : undefined;

  // BUG-V2-047: opacity-60 + grayscale pra OSs arquivadas.
  const isInativo = os.ativo === false;

  return (
    <div
      ref={setNodeRef}
      data-testid={`os-card-${os.id}`}
      data-os-status={os.status}
      data-inativo={isInativo || undefined}
      style={style}
      className={cn(
        'group relative bg-paper-surface dark:bg-paper-dark-surface rounded-lg border border-line dark:border-line-dark p-3 cursor-grab active:cursor-grabbing transition-shadow',
        isDragging ? 'opacity-50 shadow-lg' : 'hover:shadow-sm',
        isInativo && 'opacity-60 grayscale'
      )}
      {...attributes}
      {...listeners}
    >
      {/* Botão overlay invisível pra click → abrir detalhe (igual ao DealCard) */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpen?.(os);
        }}
        className="absolute inset-0 z-[1] cursor-pointer"
        aria-label={`Abrir OS ${os.titulo}`}
      />

      <div className="relative pointer-events-none">
        {/* BUG-V2-045: #N humano discreto no topo. Fallback ausente p/ OSs
            pré-migration 050 (humanLabel IS NULL).
            BUG-V2-047: badge "Inativo" inline quando arquivado. */}
        {(os.humanLabel || isInativo) && (
          <div className="flex items-center gap-1.5 mb-0.5">
            {os.humanLabel && (
              <p
                className="text-xs font-mono text-ink-3 dark:text-ink-dark-3"
                data-testid={`os-card-${os.id}-label`}
              >
                {os.humanLabel}
              </p>
            )}
            {isInativo && (
              <Badge variant="neutral" size="sm" data-testid={`os-card-${os.id}-inativo`}>
                Inativo
              </Badge>
            )}
          </div>
        )}
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-sm font-medium text-ink-1 dark:text-ink-dark-1 line-clamp-2">
            {os.titulo}
          </h3>
          {os.prioridade !== 'normal' && (
            <Badge
              variant={
                os.prioridade === 'urgente'
                  ? 'danger'
                  : os.prioridade === 'alta'
                    ? 'warning'
                    : 'neutral'
              }
            >
              {PRIORIDADE_LABELS[os.prioridade as OsPrioridade]}
            </Badge>
          )}
        </div>
        {/* BUG-V2-058: stack de tags no header (max 3 + overflow). */}
        {os.tags && os.tags.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-1 mb-1"
            data-testid={`os-card-${os.id}-tags`}
          >
            {os.tags.slice(0, 3).map((tag) => (
              <TagBadge key={tag.id} tag={tag} size="sm" />
            ))}
            {os.tags.length > 3 && (
              <Badge
                variant="neutral"
                size="sm"
                title={os.tags
                  .slice(3)
                  .map((t) => t.nome)
                  .join(', ')}
              >
                +{os.tags.length - 3}
              </Badge>
            )}
          </div>
        )}
        <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-2 truncate">{os.endereco}</p>
        <div className="flex items-center justify-between text-xs text-ink-3 dark:text-ink-dark-3">
          {/* FASE-C: emoji → Lucide (👷/⏳ fora). */}
          <span className="inline-flex items-center gap-1 min-w-0">
            {tecnicoNome ? (
              <HardHat className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            ) : (
              <UserRound className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            )}
            <span className="truncate">{tecnicoNome ?? 'Sem técnico'}</span>
          </span>
          <span className="shrink-0">{formatRelativeTime(os.criadoEmIso)}</span>
        </div>
        {os.agendadoParaIso && (
          <div className="inline-flex items-center gap-1 text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
            <CalendarDays className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            {formatDataHoraSegundos(os.agendadoParaIso)}
          </div>
        )}
        {os.clienteAprovou && (
          <Badge variant={column.variant} className="mt-1">
            <Check className="w-3 h-3 mr-0.5" aria-hidden="true" />
            Cliente aprovou
          </Badge>
        )}
      </div>
    </div>
  );
}

// ─── Detalhe simples — placeholder até FEAT-V2-007 completo ────────────

// OsDetailDialog (stub) substituído por src/pages/menu/manutencao/OsDetailDialog.tsx
// (UX-1 / FEAT-V2-007). Import no topo do arquivo.

// ─── Modal "+ Nova OS" ──────────────────────────────────────────────────

interface NovaOsDialogProps {
  workflowId: string;
  onClose: () => void;
}

function NovaOsDialog({ workflowId, onClose }: NovaOsDialogProps) {
  const { create, pending } = useServiceOrderActions();
  const users = useUsers();
  const [clientId, setClientId] = useState('');
  const [clientQuery, setClientQuery] = useState('');
  const { data: clientResults = [] } = useClientsSearch({ q: clientQuery });
  const [tipo, setTipo] = useState('preventiva');
  const [titulo, setTitulo] = useState('');
  const [endereco, setEndereco] = useState('');
  const [prioridade, setPrioridade] = useState<OsPrioridade>('normal');
  const [tecnicoId, setTecnicoId] = useState<string>('');
  const [agendadoPara, setAgendadoPara] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Técnicos disponíveis: users ativos do departamento manutencao
  const tecnicosDisponiveis = useMemo(
    () =>
      users.filter(
        (u) =>
          u.ativo &&
          (u.departamentos?.some((dr) => dr.departmentSlug === 'manutencao') ?? false)
      ),
    [users]
  );

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!clientId) {
      setError('Selecione um cliente');
      return;
    }
    try {
      await create({
        workflowId,
        clienteId: clientId,
        tecnicoId: tecnicoId || null,
        tipo,
        titulo,
        endereco,
        prioridade,
        agendadoParaIso: agendadoPara ? new Date(agendadoPara).toISOString() : null,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar OS');
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="md">
      <DialogHeader>
        <DialogTitle>Nova Ordem de Serviço</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert variant="danger" rail className="mb-3">
              {error}
            </Alert>
          )}
          <div className="space-y-3">
            <div>
              <Label htmlFor="os-titulo" required>
                Título
              </Label>
              <Input
                id="os-titulo"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ex: Manutenção CFTV trimestral"
                required
                data-testid="os-form-titulo"
              />
            </div>
            <div>
              <Label htmlFor="os-cliente-search" required>
                Cliente
              </Label>
              <Input
                id="os-cliente-search"
                value={clientQuery}
                onChange={(e) => setClientQuery(e.target.value)}
                placeholder="Buscar cliente..."
              />
              {clientResults.length > 0 && (
                <ul className="mt-1 max-h-32 overflow-y-auto border border-line dark:border-line-dark rounded-lg">
                  {clientResults.slice(0, 10).map((c: Client) => (
                    <li
                      key={c.id}
                      onClick={() => {
                        setClientId(c.id);
                        setClientQuery(c.nome);
                      }}
                      className={`px-3 py-1.5 text-xs cursor-pointer hover:bg-paper-warm dark:hover:bg-paper-dark-warm ${
                        clientId === c.id ? 'bg-red-soft dark:bg-red-soft-dark' : ''
                      }`}
                      data-testid={`os-cliente-option-${c.id}`}
                    >
                      {c.nome}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <Label htmlFor="os-endereco" required>
                Endereço
              </Label>
              <Input
                id="os-endereco"
                value={endereco}
                onChange={(e) => setEndereco(e.target.value)}
                placeholder="Rua, número, bairro, cidade"
                required
                data-testid="os-form-endereco"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="os-tipo">Tipo</Label>
                <Input
                  id="os-tipo"
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value)}
                  placeholder="preventiva / corretiva / etc"
                />
              </div>
              <div>
                <Label htmlFor="os-prioridade">Prioridade</Label>
                <Select
                  id="os-prioridade"
                  value={prioridade}
                  onChange={(e) => setPrioridade(e.target.value as OsPrioridade)}
                >
                  {(Object.keys(PRIORIDADE_LABELS) as OsPrioridade[]).map((p) => (
                    <option key={p} value={p}>
                      {PRIORIDADE_LABELS[p]}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="os-tecnico">Técnico (opcional)</Label>
                <Select
                  id="os-tecnico"
                  value={tecnicoId}
                  onChange={(e) => setTecnicoId(e.target.value)}
                >
                  <option value="">— Sem atribuição —</option>
                  {tecnicosDisponiveis.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label>Agendado pra</Label>
                <Calendario
                  valor={agendadoPara}
                  onChange={setAgendadoPara}
                  placeholder="Escolher data e hora"
                  testId="os-agendado-calendario"
                />
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={pending === 'create'}
            disabled={!titulo || !endereco || !clientId}
          >
            Criar OS
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
